import { isPlanErrorType, reportPlanError } from './plan-errors';

const getApiBaseUrl = () => {

  // Fallback: extract from GraphQL endpoint or use default
  const graphqlUrl = process.env.VUE_APP_GRAPHQL_HTTP || '';
  if (graphqlUrl) {
    const url = new URL(graphqlUrl);
    return `${url.protocol}//${url.host}`;
  }

  // Default fallback
  return 'http://localhost:8003';
};

/**
 * Get JWT token from localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('apollo-done-token');
};

/**
 * Check subscription status
 * @returns {Promise<Object>} Subscription status response
 */
export async function checkSubscriptionStatus() {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const token = getAuthToken();
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${apiBaseUrl}/api/client/subscriptions/status`, {
      method: 'POST',
      headers: headers,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      data: data,
    };
  } catch (error) {
    console.error('Subscription status check failed:', error);
    return {
      success: false,
      error: error.message,
      data: null,
    };
  }
}

/**
 * Whether the paywall should overlay the app. Backend decides this in
 * /api/client/subscriptions/status — paywall_active is true when a bill has
 * been generated and the 3-day grace period has elapsed without payment, or
 * when the subscription is hard-expired with no pending bill.
 *
 * @param {Object} subscriptionData - Subscription status data from API
 * @returns {boolean}
 */
export function isSubscriptionExpired(subscriptionData) {
  if (!subscriptionData) return true;
  if (subscriptionData.has_subscription === false) return true;
  return subscriptionData.paywall_active === true;
}

/**
 * Initiate payment for subscription
 * @param {string} subscriptionId - Subscription ID
 * @param {string} clientId - Client ID
 * @param {number} amount - Payment amount
 * @returns {Promise<Object>} Payment initiation response
 */
export async function initiatePayment(subscriptionId, clientId, amount) {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const token = getAuthToken();
    
    // Get current page URL for redirect after payment
    const redirectUrl = typeof window !== 'undefined' ? window.location.origin : '';
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${apiBaseUrl}/api/client/payments/initiate`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        subscription_id: subscriptionId,
        client_id: clientId,
        amount: amount,
        redirect_url: redirectUrl, // Send client frontend URL for redirect after payment
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      reportRestPlanError(response.status, errorData, 'mutation');
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      data: data,
    };
  } catch (error) {
    console.error('Payment initiation failed:', error);
    return {
      success: false,
      error: error.message,
      data: null,
    };
  }
}

/**
 * A plan-gate refusal on a REST call arrives as 402/423 with a `{ type, ... }` body
 * (SAAS-REVAMP/DESIGN.md §8.1): raise the upgrade dialog for it.
 */
function reportRestPlanError(status, body, operationType) {
  if ((status === 402 || status === 423) && body && isPlanErrorType(body.type)) {
    reportPlanError(body, { operationType });
  }
}

/**
 * POST to one of the tenant SaaS endpoints diet-api proxies to the SaaS backend
 * (/api/client/saas/*, DESIGN §6.2/§6.5) — same origin, token and headers as the status call
 * (x-tenant-id is added by the fetch interceptor). Never throws; until the backend ships an
 * endpoint it answers 404, which callers show as "not available yet".
 * @returns {Promise<{success: boolean, status: number, data: Object|null, error: string|null}>}
 */
async function postClientSaas(path, body, operationType) {
  try {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = 'Bearer ' + token;
    }
    const response = await fetch(getApiBaseUrl() + '/api/client/saas/' + path, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body || {}),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      reportRestPlanError(response.status, data, operationType);
      return {
        success: false,
        status: response.status,
        data: data,
        error: (data && (data.error || data.message)) || 'HTTP ' + response.status,
      };
    }
    return { success: true, status: response.status, data: data, error: null };
  } catch (error) {
    // network failure (or a 401/403 the fetch interceptor already turned into a logout)
    return { success: false, status: 0, data: null, error: error.message };
  }
}

/** `{ entitlements, catalog: { features, groups, limits } }` for the signed-in tenant. */
export function fetchPlanEntitlements() {
  return postClientSaas('entitlements', {}, 'query');
}

/** `{ currency, current_plan_id, plans: [...] }` — the base plans priced for the tenant. */
export function fetchPlanOptions() {
  return postClientSaas('plans', {}, 'query');
}

/** Asks the SaaS team for a plan change; both fields optional. Resolves `{ id, status }` on success. */
export function requestPlanUpgrade({ planId, message } = {}) {
  const body = {};
  if (planId) body.plan_id = planId;
  if (message) body.message = message;
  return postClientSaas('upgrade-requests', body, 'mutation');
}

/** Hides an announcement for this tenant (the SaaS backend stops returning it). */
export function dismissAnnouncement(id) {
  return postClientSaas('announcements/' + encodeURIComponent(id) + '/dismiss', {}, 'mutation');
}